/**
 * Iter XX.75 · Phase 1 — Practice with Gully in-app recorder.
 *
 * Full-screen recorder replacing the file-picker path. Mounts a live
 * camera preview via getUserMedia, records via MediaRecorder in
 * 1-sec timeslices, and hands the final blob back to the parent so
 * PracticeStarter can drive the (unchanged) upload → analyse pipeline.
 *
 * Design points (see the pre-implementation write-up):
 *   • codec is browser-decided (mp4 on iOS, webm on Android) — we
 *     just carry the mime forward
 *   • no pause/resume (inconsistent on iOS)
 *   • no crash-recovery via IndexedDB (out of scope)
 *   • auto-stop at 2 min
 *   • if MediaRecorder / getUserMedia is unavailable, we DO NOT render
 *     this component — PracticeStarter routes to the file picker
 *     instead (feature-detected in `canUseInAppRecorder()`)
 *   • recorded blob stays in memory so upload retries never require
 *     re-recording
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Circle, Square, RefreshCw, RotateCw, Video } from "lucide-react";

const MAX_RECORDING_SECONDS = 120;

const MIME_CANDIDATES = [
  "video/mp4;codecs=h264,aac",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
];

function pickSupportedMime() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const c of MIME_CANDIDATES) {
    try {
      if (window.MediaRecorder.isTypeSupported(c)) return c;
    } catch (_) { /* keep trying */ }
  }
  return null;
}

export function canUseInAppRecorder() {
  try {
    if (typeof window === "undefined") return false;
    if (!navigator?.mediaDevices?.getUserMedia) return false;
    if (!window.MediaRecorder) return false;
    if (!pickSupportedMime()) return false;
    return true;
  } catch (_) {
    return false;
  }
}

function fmtDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Props:
 *   onCancel()         — close without a blob
 *   onComplete(blob, mime) — user tapped "Use Video"
 *   onEvent?(name, meta) — optional analytics hook
 */
export default function PracticeRecorder({ onCancel, onComplete, onEvent }) {
  const [phase, setPhase] = useState("initializing");
  // initializing | recording | preview | error
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blobSize, setBlobSize] = useState(0);

  const videoRef = useRef(null);          // live preview element
  const previewRef = useRef(null);        // preview element (after stop)
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickerRef = useRef(null);
  const autoStopRef = useRef(null);
  const finalBlobRef = useRef(null);
  const cancelledRef = useRef(false);

  const emit = (name, meta) => {
    try { onEvent?.(name, meta || {}); } catch (_) { /* noop */ }
  };

  // ---- Camera setup / teardown -----------------------------------------

  async function acquireStream(currentFacing) {
    // Stop any existing stream first (camera switch path).
    stopStream();
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: currentFacing },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: true,
      });
    } catch (err) {
      // If the ideal rear camera isn't available (e.g. laptop), retry
      // without a facingMode constraint before giving up.
      if (currentFacing === "environment") {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true, audio: true,
          });
        } catch (err2) {
          throw err2;
        }
      } else {
        throw err;
      }
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      // On iOS Safari the play() promise must be awaited or the element
      // may stay black. We swallow errors — the visible fallback is
      // "tap Start Recording", which the user will do anyway.
      try { await videoRef.current.play(); } catch (_) { /* noop */ }
    }
  }

  function stopStream() {
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch (_) { /* noop */ }
    streamRef.current = null;
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (_) { /* noop */ }
    }
  }

  function classifyGumError(err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      return {
        code: "permission_denied",
        message: "Camera access was blocked. In your phone's Settings, allow camera and microphone for this site, then try again.",
      };
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      return {
        code: "no_camera",
        message: "We couldn't find a camera on this device. You can still upload an existing video instead.",
      };
    }
    if (name === "NotReadableError" || name === "AbortError") {
      return {
        code: "camera_busy",
        message: "Your camera looks busy. Close any other app using it and try again.",
      };
    }
    return {
      code: name || "unknown",
      message: "Couldn't open the camera. Try closing other tabs, then tap Retry.",
    };
  }

  // Mount — open the camera immediately so the prep view feels live.
  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      try {
        await acquireStream(facingMode);
        if (cancelledRef.current) return;
        setPhase("recording"); // start-recording button appears from here
      } catch (err) {
        if (cancelledRef.current) return;
        const c = classifyGumError(err);
        setErrorCode(c.code);
        setErrorMessage(c.message);
        setPhase("error");
        emit("practice_recorder_permission_denied", { code: c.code });
      }
    })();
    return () => {
      cancelledRef.current = true;
      cleanup();
    };
    // Intentionally omitted deps: acquireStream is stable per-call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanup() {
    try { if (autoStopRef.current) clearTimeout(autoStopRef.current); } catch (_) { /* noop */ }
    try { if (tickerRef.current) clearInterval(tickerRef.current); } catch (_) { /* noop */ }
    autoStopRef.current = null;
    tickerRef.current = null;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (_) { /* noop */ }
    stopStream();
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (_) { /* noop */ }
    }
  }

  // ---- Recording -------------------------------------------------------

  async function handleStartRecording() {
    if (!streamRef.current) return;
    const mime = pickSupportedMime();
    if (!mime) {
      setErrorCode("mime_unavailable");
      setErrorMessage("This browser can't record video. Try Safari or Chrome, or upload an existing video instead.");
      setPhase("error");
      return;
    }
    mimeRef.current = mime;
    chunksRef.current = [];
    finalBlobRef.current = null;

    let recorder;
    try {
      recorder = new window.MediaRecorder(streamRef.current, {
        mimeType: mime,
        videoBitsPerSecond: 2_500_000,
      });
    } catch (err) {
      // Some browsers reject the bitrate hint; retry without it.
      try {
        recorder = new window.MediaRecorder(streamRef.current, { mimeType: mime });
      } catch (err2) {
        setErrorCode("recorder_init_failed");
        setErrorMessage("Couldn't start the recorder. Try closing other tabs, then tap Retry.");
        setPhase("error");
        return;
      }
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e?.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onerror = () => {
      emit("practice_recorder_error", { where: "recorder_error_event" });
      finishRecording("interrupted");
    };
    recorder.onstop = () => {
      // Called after our stop() OR after an unexpected termination.
      const blob = new Blob(chunksRef.current, {
        type: mimeRef.current || "video/mp4",
      });
      finalBlobRef.current = blob;
      setBlobSize(blob.size);
      // Cleanup timers immediately so the visible timer doesn't tick past.
      try { if (autoStopRef.current) clearTimeout(autoStopRef.current); } catch (_) { /* noop */ }
      try { if (tickerRef.current) clearInterval(tickerRef.current); } catch (_) { /* noop */ }
      autoStopRef.current = null;
      tickerRef.current = null;
      // Move to preview only if we actually captured something.
      if (blob.size === 0) {
        setErrorCode("empty_recording");
        setErrorMessage("The recording came back empty. Try again — hold still while recording starts.");
        setPhase("error");
        return;
      }
      try {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (_) { /* noop */ }
      // Free the camera as soon as we have the blob.
      stopStream();
      setPhase("preview");
      emit("practice_recorder_stopped_success", { size_bytes: blob.size });
    };

    startedAtRef.current = Date.now();
    setElapsed(0);
    try {
      recorder.start(1000); // 1-sec timeslices for resilience
    } catch (err) {
      setErrorCode("recorder_start_failed");
      setErrorMessage("Recording couldn't start. Tap Retry.");
      setPhase("error");
      return;
    }
    emit("practice_recorder_started", { mime });

    tickerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(secs);
    }, 500);

    autoStopRef.current = setTimeout(() => {
      emit("practice_recorder_auto_stopped", { reason: "max_duration" });
      finishRecording("timeout");
    }, MAX_RECORDING_SECONDS * 1000);
  }

  function finishRecording(reason) {
    emit("practice_recorder_stopping", { reason });
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop(); // fires onstop, which promotes to preview
      }
    } catch (_) { /* noop */ }
  }

  function handleStopClick() {
    finishRecording("user");
  }

  async function handleSwitchCamera() {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    try {
      await acquireStream(next);
    } catch (err) {
      const c = classifyGumError(err);
      setErrorCode(c.code);
      setErrorMessage(c.message);
      setPhase("error");
    }
  }

  // ---- Preview actions -------------------------------------------------

  function handleRetake() {
    emit("practice_recorder_retake");
    // Revoke old preview URL, clear blob, re-open camera.
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (_) { /* noop */ }
    }
    setPreviewUrl(null);
    finalBlobRef.current = null;
    chunksRef.current = [];
    setBlobSize(0);
    setElapsed(0);
    setPhase("initializing");
    (async () => {
      try {
        await acquireStream(facingMode);
        setPhase("recording");
      } catch (err) {
        const c = classifyGumError(err);
        setErrorCode(c.code);
        setErrorMessage(c.message);
        setPhase("error");
      }
    })();
  }

  function handleUseVideo() {
    const blob = finalBlobRef.current;
    if (!blob) return;
    emit("practice_recorder_use_video", {
      size_bytes: blob.size, duration_sec: elapsed,
    });
    onComplete?.(blob, mimeRef.current || blob.type || "video/mp4");
  }

  function handleErrorRetry() {
    setErrorCode("");
    setErrorMessage("");
    setPhase("initializing");
    (async () => {
      try {
        await acquireStream(facingMode);
        setPhase("recording");
      } catch (err) {
        const c = classifyGumError(err);
        setErrorCode(c.code);
        setErrorMessage(c.message);
        setPhase("error");
      }
    })();
  }

  // ---- Render ----------------------------------------------------------

  const isRecording = phase === "recording"
    && recorderRef.current
    && recorderRef.current.state === "recording";

  return (
    <div
      className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
      data-testid="practice-recorder-root"
    >
      {/* Live video / preview surface */}
      {phase !== "preview" && (
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          className="absolute inset-0 w-full h-full object-cover bg-black"
          data-testid="practice-recorder-live-video"
        />
      )}
      {phase === "preview" && previewUrl && (
        <video
          ref={previewRef}
          src={previewUrl}
          controls
          playsInline
          className="absolute inset-0 w-full h-full object-contain bg-black"
          data-testid="practice-recorder-preview-video"
        />
      )}

      {/* Top bar — close */}
      <div className="absolute top-0 inset-x-0 flex justify-between items-start p-3 pointer-events-none">
        <button
          onClick={() => { emit("practice_recorder_cancel", { phase }); onCancel?.(); }}
          className="pointer-events-auto bg-black/60 backdrop-blur rounded-full p-2 text-white hover:bg-black/80"
          aria-label="Close recorder"
          data-testid="practice-recorder-close"
        >
          <X size={20} />
        </button>

        {/* Timer badge (recording only) */}
        {isRecording && (
          <div
            className="pointer-events-auto flex items-center gap-2 bg-red-600/90 rounded-full px-3 py-1.5 text-white text-sm font-mono shadow-lg"
            data-testid="practice-recorder-timer"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
            {fmtDuration(elapsed)}
            <span className="opacity-70">
              / {fmtDuration(MAX_RECORDING_SECONDS)}
            </span>
          </div>
        )}

        {/* Camera switch (recording phase, not while recording) */}
        {phase === "recording" && !isRecording && (
          <button
            onClick={handleSwitchCamera}
            className="pointer-events-auto bg-black/60 backdrop-blur rounded-full p-2 text-white hover:bg-black/80"
            aria-label="Switch camera"
            data-testid="practice-recorder-switch-camera"
          >
            <RotateCw size={18} />
          </button>
        )}
      </div>

      {/* Bottom control bar */}
      <div className="absolute bottom-0 inset-x-0 px-4 pb-6 pt-8 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
        {phase === "initializing" && (
          <div
            className="text-center text-white/80 text-sm py-4"
            data-testid="practice-recorder-initializing"
          >
            Opening your camera…
          </div>
        )}

        {phase === "recording" && !isRecording && (
          <div className="flex flex-col items-center gap-3">
            <div className="text-white/80 text-[13px] text-center max-w-sm">
              Frame your run-up side-on, then tap to start.
            </div>
            <button
              onClick={handleStartRecording}
              className="w-20 h-20 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center shadow-2xl ring-4 ring-white/20 active:scale-95 transition-transform"
              aria-label="Start recording"
              data-testid="practice-recorder-start-btn"
            >
              <Circle size={32} className="text-white fill-white" />
            </button>
          </div>
        )}

        {phase === "recording" && isRecording && (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleStopClick}
              className="w-20 h-20 rounded-full bg-white hover:bg-neutral-200 flex items-center justify-center shadow-2xl ring-4 ring-red-600/60 active:scale-95 transition-transform"
              aria-label="Stop recording"
              data-testid="practice-recorder-stop-btn"
            >
              <Square size={26} className="text-red-600 fill-red-600" />
            </button>
            <div className="text-white/70 text-[11px]">
              Stops automatically at {fmtDuration(MAX_RECORDING_SECONDS)}.
            </div>
          </div>
        )}

        {phase === "preview" && (
          <div
            className="flex flex-col gap-3"
            data-testid="practice-recorder-preview-controls"
          >
            <div className="text-center text-white/70 text-[12px]">
              {fmtDuration(elapsed)} · {fmtBytes(blobSize)}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRetake}
                className="flex-1 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-3 flex items-center justify-center gap-2"
                data-testid="practice-recorder-retake"
              >
                <RefreshCw size={16} />
                Retake
              </button>
              <button
                onClick={handleUseVideo}
                className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-3 flex items-center justify-center gap-2"
                data-testid="practice-recorder-use-video"
              >
                <Video size={16} />
                Use Video
              </button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div
            className="flex flex-col gap-3"
            data-testid="practice-recorder-error"
          >
            <div className="rounded-xl bg-amber-950/80 border border-amber-700/70 px-4 py-3 text-[13px] text-amber-100">
              {errorMessage}
              {errorCode && (
                <div className="text-[10px] text-amber-300/70 mt-1 font-mono">
                  ({errorCode})
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { emit("practice_recorder_cancel", { phase: "error" }); onCancel?.(); }}
                className="flex-1 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm py-3"
                data-testid="practice-recorder-error-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleErrorRetry}
                className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-3"
                data-testid="practice-recorder-error-retry"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
